/**
 * Poll Reducer - Gestion centralisée de l'état du sondage
 *
 * Actions supportées :
 * - ADD_DATE : Ajouter une date au sondage
 * - REMOVE_DATE : Retirer une date du sondage
 * - UPDATE_TITLE : Modifier le titre du sondage
 * - ADD_TIMESLOT : Ajouter un créneau horaire à une date
 * - REPLACE_POLL : Remplacer complètement le sondage
 */

import { Poll } from "../lib/pollStorage"; // Utiliser le type Poll unifié de pollStorage
import { convertGeminiSlotToInternal } from "../services/TimeSlotConverter";
import { formPollReducer, type FormPollAction } from "./formPollReducer";

// Types d'actions
export type PollAction =
  | { type: "ADD_DATE"; payload: string }
  | { type: "REMOVE_DATE"; payload: string }
  | { type: "UPDATE_TITLE"; payload: string }
  | {
      type: "ADD_TIMESLOT";
      payload: { date: string; start: string; end: string };
    }
  | { type: "REPLACE_POLL"; payload: Poll }
  | { type: "CLEAR_POLL" }
  | FormPollAction; // Ajouter les actions Form Poll

/**
 * Reducer pour gérer les modifications du sondage
 */
export function pollReducer(
  state: Poll | null,
  action: PollAction,
): Poll | null {
  switch (action.type) {
    case "REPLACE_POLL": {
      // Remplacer complètement le sondage (utilisé pour l'initialisation)
      return action.payload;
    }

    case "CLEAR_POLL": {
      // Réinitialiser la proposition courante (bouton "Recommencer from
      // scratch" après que l'utilisateur a épuisé son budget de
      // modifications AI).
      return null;
    }

    case "ADD_DATE": {
      if (!state) return null;
      const newDate = action.payload;

      // Vérifier si la date existe déjà
      if (state.dates?.includes(newDate)) {
        return state; // Pas de changement
      }

      // Ajouter la date et trier
      const updatedDates = [...(state.dates || []), newDate].sort();

      return {
        ...state,
        dates: updatedDates,
        updated_at: new Date().toISOString(),
        _highlightedId: newDate,
        _highlightType: "add",
      } as import("../lib/pollStorage").Poll;
    }

    case "REMOVE_DATE": {
      if (!state) return null;
      const dateToRemove = action.payload;

      // Vérifier si la date existe
      if (!state.dates?.includes(dateToRemove)) {
        return state; // Date non trouvée, pas de changement
      }

      // Retirer la date
      const updatedDates = state.dates.filter((date) => date !== dateToRemove);

      return {
        ...state,
        dates: updatedDates,
        updated_at: new Date().toISOString(),
        _highlightedId: dateToRemove,
        _highlightType: "remove",
      } as import("../lib/pollStorage").Poll;
    }

    case "UPDATE_TITLE": {
      if (!state) return null;
      const newTitle = action.payload.trim();

      // Vérifier que le titre n'est pas vide
      if (!newTitle) {
        return state; // Pas de changement si titre vide
      }

      return {
        ...state,
        title: newTitle,
        updated_at: new Date().toISOString(),
      };
    }

    case "ADD_TIMESLOT": {
      if (!state) return null;
      const { date, start, end } = action.payload;

      // Ajouter automatiquement la date si elle n'existe pas
      const dates = state.dates || [];
      const updatedDates = dates.includes(date)
        ? dates
        : [...dates, date].sort();

      // Utiliser la même logique de conversion que Gemini (code réutilisé)
      const newSlot = convertGeminiSlotToInternal({ start, end });

      // Récupérer ou initialiser settings.timeSlotsByDate
      const currentSettings = state.settings || { showTimeSlots: false };
      const currentTimeSlots = (currentSettings as any).timeSlotsByDate || {};
      const dateSlots = currentTimeSlots[date] || [];

      // Modèle BLOC : 1 plage = 1 seul slot avec sa durée pleine.
      // On NE découpe PAS selon la granularité (la granularité ne sert qu'à
      // positionner la grille d'affichage, pas à dimensionner le créneau).
      const exists = dateSlots.some(
        (slot) => slot.hour === newSlot.hour && slot.minute === newSlot.minute,
      );

      if (exists) {
        return state; // Slot déjà présent à cette heure de début
      }

      const blockSlot = {
        hour: newSlot.hour,
        minute: newSlot.minute,
        duration: newSlot.duration || 60,
        enabled: true,
      };

      // Ajouter le bloc
      const updatedDateSlots = [...dateSlots, blockSlot];

      return {
        ...state,
        dates: updatedDates, // Mettre à jour les dates
        settings: {
          showTimeSlots: true,
          ...currentSettings,
          timeSlotsByDate: {
            ...currentTimeSlots,
            [date]: updatedDateSlots,
          },
        },
        updated_at: new Date().toISOString(),
      };
    }

    default:
      // Déléguer au formPollReducer pour les actions Form Poll
      if (state && state.type === "form") {
        return formPollReducer(state, action as FormPollAction);
      }
      return state;
  }
}
